import React, { useCallback, useState } from 'react';
import useClampedPage from '../../../hooks/useClampedPage';
import { DebouncedSearchField } from '../../commonComponents/DebouncedSearchField';
import { DsPagination } from '../../commonComponents/DsPagination';
import { ListPage } from '../../commonComponents/ListPage';
import { EditMetroAdminsDialog } from './components/EditMetroAdminsDialog';
import { MetroZipsDialog } from './components/MetroZipsDialog';
import { SalesMetrosTable } from './components/SalesMetrosTable';
import { useSalesMetrosPage } from './hooks';

/**
 * Management → Sales Metros (GOTU-6009): every sales metro, Unassigned
 * included, with the GoTu admins assigned to it, its zips, and an editor for
 * the assignment.
 *
 * Creating or removing metros and editing their zips are out of the MVP; both
 * happen in organization-service. Search and paging are client-side — see
 * `domain/SalesMetros/salesMetroRows.js` for why.
 */
export const SalesMetros = () => {
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  // One dialog at a time, each mounted only while open.
  const [metroShowingZips, setMetroShowingZips] = useState(null);
  const [metroBeingEdited, setMetroBeingEdited] = useState(null);

  const {
    rows, filteredCount, pageSize, admins, isPending, isFetching, isError,
  } = useSalesMetrosPage({ search, page });

  // Unknown while loading or failed, so neither the header nor the clamp reads
  // a placeholder 0 as "no metros".
  const knownCount = isPending || isError ? null : filteredCount;

  // Narrowing the search can leave the page past the end of the shorter list;
  // pull it back into range so the table never dead-ends on an empty slice.
  useClampedPage(page, knownCount, pageSize, setPage);

  // Page 3 of every metro is not page 3 of the matches.
  const handleSearchChange = useCallback((value) => {
    setSearch(value);
    setPage(1);
  }, []);

  return (
    <ListPage
      title="Sales Metros"
      isLoading={isFetching && rows.length > 0}
      total={knownCount ?? undefined}
      filters={(
        <DebouncedSearchField
          label="Search"
          placeholder="Search by metro, admin or zip code"
          value={search}
          onChange={handleSearchChange}
        />
      )}
    >
      <SalesMetrosTable
        metros={rows}
        isPending={isPending}
        isError={isError}
        hasSearch={search !== ''}
        onViewZips={setMetroShowingZips}
        onEditAdmins={setMetroBeingEdited}
        footer={(
          <DsPagination
            page={page}
            pageSize={pageSize}
            totalCount={filteredCount}
            onPageChange={setPage}
          />
        )}
      />

      {metroShowingZips && (
        <MetroZipsDialog metro={metroShowingZips} onClose={() => setMetroShowingZips(null)} />
      )}

      {metroBeingEdited && (
        <EditMetroAdminsDialog
          metro={metroBeingEdited}
          admins={admins}
          onClose={() => setMetroBeingEdited(null)}
        />
      )}
    </ListPage>
  );
};

export default SalesMetros;
